import {
    SegmentId,
    SEGMENTS,
    SEGMENTS_CRITERIA,
    SEGMENTS_SIMPLIFIED_CRITERIA,
    WalletUserAbstraction,
} from '@/lib/types/hypertrackerTypes';
import { LEADERBOARDS } from '@/state/hypertracker/hypertrackerTypes';

export const segmentsLabels = {
    [SegmentId.Leviathan]: SEGMENTS.LEVIATHAN,
    [SegmentId.Shrimp]: SEGMENTS.SHRIMP,
    [SegmentId.Fish]: SEGMENTS.FISH,
    [SegmentId.Dolphin]: SEGMENTS.DOLPHIN,
    [SegmentId.TidalWhale]: SEGMENTS.TIDAL_WHALE,
    [SegmentId.Whale]: SEGMENTS.WHALE,
    [SegmentId.SmallWhale]: SEGMENTS.SMALL_WHALE,
    [SegmentId.ApexPredator]: SEGMENTS.APEX_PREDATOR,
    [SegmentId.MoneyPrinter]: SEGMENTS.MONEY_PRINTER,
    [SegmentId.SmartMoney]: SEGMENTS.SMART_MONEY,
    [SegmentId.ConsistentGrinder]: SEGMENTS.GRINDER,
    [SegmentId.HumbleEarner]: SEGMENTS.HUMBLE_EARNER,
    [SegmentId.ExitLiquidity]: SEGMENTS.EXIT_LIQUIDATION,
    [SegmentId.SemiRekt]: SEGMENTS.SEMI_REKT,
    [SegmentId.FullRekt]: SEGMENTS.FULL_REKT,
    [SegmentId.GigaRekt]: SEGMENTS.GIGA_RECT,
};

export const segmentsCriteriaLabels = {
    [SegmentId.Leviathan]: SEGMENTS_CRITERIA.LEVIATHAN,
    [SegmentId.Shrimp]: SEGMENTS_CRITERIA.SHRIMP,
    [SegmentId.Fish]: SEGMENTS_CRITERIA.FISH,
    [SegmentId.Dolphin]: SEGMENTS_CRITERIA.DOLPHIN,
    [SegmentId.TidalWhale]: SEGMENTS_CRITERIA.TIDAL_WHALE,
    [SegmentId.Whale]: SEGMENTS_CRITERIA.WHALE,
    [SegmentId.SmallWhale]: SEGMENTS_CRITERIA.SMALL_WHALE,
    [SegmentId.ApexPredator]: SEGMENTS_CRITERIA.APEX_PREDATOR,
    [SegmentId.MoneyPrinter]: SEGMENTS_CRITERIA.MONEY_PRINTER,
    [SegmentId.SmartMoney]: SEGMENTS_CRITERIA.SMART_MONEY,
    [SegmentId.ConsistentGrinder]: SEGMENTS_CRITERIA.GRINDER,
    [SegmentId.HumbleEarner]: SEGMENTS_CRITERIA.HUMBLE_EARNER,
    [SegmentId.ExitLiquidity]: SEGMENTS_CRITERIA.EXIT_LIQUIDATION,
    [SegmentId.SemiRekt]: SEGMENTS_CRITERIA.SEMI_REKT,
    [SegmentId.FullRekt]: SEGMENTS_CRITERIA.FULL_REKT,
    [SegmentId.GigaRekt]: SEGMENTS_CRITERIA.GIGA_RECT,
};

export const segmentsSimplifiedCriteriaLabels = {
    [SegmentId.Leviathan]: SEGMENTS_SIMPLIFIED_CRITERIA.LEVIATHAN,
    [SegmentId.Shrimp]: SEGMENTS_SIMPLIFIED_CRITERIA.SHRIMP,
    [SegmentId.Fish]: SEGMENTS_SIMPLIFIED_CRITERIA.FISH,
    [SegmentId.Dolphin]: SEGMENTS_SIMPLIFIED_CRITERIA.DOLPHIN,
    [SegmentId.TidalWhale]: SEGMENTS_SIMPLIFIED_CRITERIA.TIDAL_WHALE,
    [SegmentId.Whale]: SEGMENTS_SIMPLIFIED_CRITERIA.WHALE,
    [SegmentId.SmallWhale]: SEGMENTS_SIMPLIFIED_CRITERIA.SMALL_WHALE,
    [SegmentId.ApexPredator]: SEGMENTS_SIMPLIFIED_CRITERIA.APEX_PREDATOR,
    [SegmentId.MoneyPrinter]: SEGMENTS_SIMPLIFIED_CRITERIA.MONEY_PRINTER,
    [SegmentId.SmartMoney]: SEGMENTS_SIMPLIFIED_CRITERIA.SMART_MONEY,
    [SegmentId.ConsistentGrinder]: SEGMENTS_SIMPLIFIED_CRITERIA.GRINDER,
    [SegmentId.HumbleEarner]: SEGMENTS_SIMPLIFIED_CRITERIA.HUMBLE_EARNER,
    [SegmentId.ExitLiquidity]: SEGMENTS_SIMPLIFIED_CRITERIA.EXIT_LIQUIDATION,
    [SegmentId.SemiRekt]: SEGMENTS_SIMPLIFIED_CRITERIA.SEMI_REKT,
    [SegmentId.FullRekt]: SEGMENTS_SIMPLIFIED_CRITERIA.FULL_REKT,
    [SegmentId.GigaRekt]: SEGMENTS_SIMPLIFIED_CRITERIA.GIGA_RECT,
};

/** Numeric bounds for `segmentsSimplifiedCriteriaLabels` — kept separate so range values
 * can be read without parsing the translated (locale-formatted) label text. */
export const segmentsSimplifiedCriteriaRanges: Partial<
    Record<SegmentId, { from: number; to?: number }>
> = {
    [SegmentId.Leviathan]: { from: 5000000 },
    [SegmentId.TidalWhale]: { from: 1000000, to: 5000000 },
    [SegmentId.Whale]: { from: 500000, to: 1000000 },
    [SegmentId.SmallWhale]: { from: 100000, to: 500000 },
    [SegmentId.ApexPredator]: { from: 50000, to: 100000 },
    [SegmentId.Dolphin]: { from: 10000, to: 50000 },
    [SegmentId.Fish]: { from: 250, to: 10000 },
    [SegmentId.Shrimp]: { from: 0, to: 250 },
    [SegmentId.MoneyPrinter]: { from: 1000000 },
    [SegmentId.SmartMoney]: { from: 100000, to: 1000000 },
    [SegmentId.ConsistentGrinder]: { from: 10000, to: 100000 },
    [SegmentId.HumbleEarner]: { from: 0, to: 10000 },
    [SegmentId.ExitLiquidity]: { from: 0, to: -10000 },
    [SegmentId.SemiRekt]: { from: -10000, to: -100000 },
    [SegmentId.FullRekt]: { from: -100000, to: -1000000 },
    [SegmentId.GigaRekt]: { from: -1000000 },
};

export const labelsSegments = {
    [SEGMENTS.LEVIATHAN]: SegmentId.Leviathan,
    [SEGMENTS.SHRIMP]: SegmentId.Shrimp,
    [SEGMENTS.FISH]: SegmentId.Fish,
    [SEGMENTS.DOLPHIN]: SegmentId.Dolphin,
    [SEGMENTS.TIDAL_WHALE]: SegmentId.TidalWhale,
    [SEGMENTS.WHALE]: SegmentId.Whale,
    [SEGMENTS.SMALL_WHALE]: SegmentId.SmallWhale,
    [SEGMENTS.APEX_PREDATOR]: SegmentId.ApexPredator,
    [SEGMENTS.MONEY_PRINTER]: SegmentId.MoneyPrinter,
    [SEGMENTS.SMART_MONEY]: SegmentId.SmartMoney,
    [SEGMENTS.GRINDER]: SegmentId.ConsistentGrinder,
    [SEGMENTS.HUMBLE_EARNER]: SegmentId.HumbleEarner,
    [SEGMENTS.EXIT_LIQUIDATION]: SegmentId.ExitLiquidity,
    [SEGMENTS.SEMI_REKT]: SegmentId.SemiRekt,
    [SEGMENTS.FULL_REKT]: SegmentId.FullRekt,
    [SEGMENTS.GIGA_RECT]: SegmentId.GigaRekt,
};

export const segmentLabelToId: Record<string, SegmentId> = {
    leviathan: SegmentId.Leviathan,
    'tidal-whale': SegmentId.TidalWhale,
    whale: SegmentId.Whale,
    'small-whale': SegmentId.SmallWhale,
    'apex-predator': SegmentId.ApexPredator,
    dolphin: SegmentId.Dolphin,
    fish: SegmentId.Fish,
    shrimp: SegmentId.Shrimp,
    'money-printer': SegmentId.MoneyPrinter,
    'smart-money': SegmentId.SmartMoney,
    grinder: SegmentId.ConsistentGrinder,
    'humble-earner': SegmentId.HumbleEarner,
    'exit-liquidity': SegmentId.ExitLiquidity,
    'semi-rekt': SegmentId.SemiRekt,
    'full-rekt': SegmentId.FullRekt,
    'giga-rekt': SegmentId.GigaRekt,
};

export const segmentIdToLabel: Record<SegmentId, string> = Object.entries(segmentLabelToId).reduce(
    (acc, [label, id]) => {
        acc[id] = label;
        return acc;
    },
    {} as Record<SegmentId, string>,
);

export const defineEquityDescription = (value: number) => {
    if (value >= 0 && value < 250) {
        return { label: SEGMENTS.SHRIMP, range: SEGMENTS_CRITERIA.SHRIMP };
    } else if (value >= 250 && value < 10000) {
        return { label: SEGMENTS.FISH, range: SEGMENTS_CRITERIA.FISH };
    } else if (value >= 10000 && value < 50000) {
        return { label: SEGMENTS.DOLPHIN, range: SEGMENTS_CRITERIA.DOLPHIN };
    } else if (value >= 50000 && value < 100000) {
        return {
            label: SEGMENTS.APEX_PREDATOR,
            range: SEGMENTS_CRITERIA.APEX_PREDATOR,
        };
    } else if (value >= 100000 && value < 500000) {
        return {
            label: SEGMENTS.SMALL_WHALE,
            range: SEGMENTS_CRITERIA.SMALL_WHALE,
        };
    } else if (value >= 500000 && value < 1000000) {
        return { label: SEGMENTS.WHALE, range: SEGMENTS_CRITERIA.WHALE };
    } else if (value >= 1000000 && value < 5000000) {
        return { label: SEGMENTS.TIDAL_WHALE, range: SEGMENTS_CRITERIA.TIDAL_WHALE };
    } else if (value >= 5000000) {
        return { label: SEGMENTS.LEVIATHAN, range: SEGMENTS_CRITERIA.LEVIATHAN };
    } else {
        return { label: SEGMENTS.FISH, range: SEGMENTS_CRITERIA.FISH };
    }
};

export const definePnlDescription = (value: number) => {
    if (value >= 1000000) {
        return { label: SEGMENTS.MONEY_PRINTER, range: SEGMENTS_CRITERIA.MONEY_PRINTER };
    } else if (value >= 100000 && value < 1000000) {
        return { label: SEGMENTS.SMART_MONEY, range: SEGMENTS_CRITERIA.SMART_MONEY };
    } else if (value >= 10000 && value < 100000) {
        return { label: SEGMENTS.GRINDER, range: SEGMENTS_CRITERIA.GRINDER };
    } else if (value >= 0 && value < 10000) {
        return { label: SEGMENTS.HUMBLE_EARNER, range: SEGMENTS_CRITERIA.HUMBLE_EARNER };
    } else if (value >= -10000 && value < 0) {
        return { label: SEGMENTS.EXIT_LIQUIDATION, range: SEGMENTS_CRITERIA.EXIT_LIQUIDATION };
    } else if (value >= -100000 && value < -10000) {
        return { label: SEGMENTS.SEMI_REKT, range: SEGMENTS_CRITERIA.SEMI_REKT };
    } else if (value >= -1000000 && value < -100000) {
        return { label: SEGMENTS.FULL_REKT, range: SEGMENTS_CRITERIA.FULL_REKT };
    } else if (value < -1000000) {
        return { label: SEGMENTS.GIGA_RECT, range: SEGMENTS_CRITERIA.GIGA_RECT };
    } else {
        return { label: SEGMENTS.EXIT_LIQUIDATION, range: SEGMENTS_CRITERIA.EXIT_LIQUIDATION };
    }
};

const UNIFIED_MARGIN_ASSET = 'USDC';
const PORTFOLIO_MARGIN_ASSETS = ['USDC', 'USDH', 'HYPE', 'UBTC'];

export interface HlCohortPerpState {
    collateral: string;
    unrealizedPnl: number;
}

export interface HlCohortSpotBalance {
    coin: string;
    total: number;
    supplied: number;
    price: number;
}

const sum = (values: number[]): number => values.reduce((acc, value) => acc + value, 0);

export const getCohortingEquity = ({
    userAbstraction,
    perpEquity,
    perpStates,
    spotBalances,
}: {
    userAbstraction: WalletUserAbstraction | null;
    perpEquity: number;
    perpStates: HlCohortPerpState[];
    spotBalances: HlCohortSpotBalance[];
}): number => {
    if (userAbstraction === WalletUserAbstraction.UNIFIED_ACCOUNT) {
        const spotMargin = sum(
            spotBalances
                .filter(({ coin }) => coin === UNIFIED_MARGIN_ASSET)
                .map(({ total, supplied, price }) => (total - supplied) * price),
        );
        const perpPnl = sum(
            perpStates
                .filter(({ collateral }) => collateral === UNIFIED_MARGIN_ASSET)
                .map(({ unrealizedPnl }) => unrealizedPnl),
        );
        return spotMargin + perpPnl;
    }

    if (userAbstraction === WalletUserAbstraction.PORTFOLIO_MARGIN) {
        const spotMargin = sum(
            spotBalances
                .filter(({ coin }) => PORTFOLIO_MARGIN_ASSETS.includes(coin))
                .map(({ total, price }) => total * price),
        );
        return spotMargin + sum(perpStates.map(({ unrealizedPnl }) => unrealizedPnl));
    }

    return perpEquity;
};

export const getSegmentsFromHlData = ({
    userAbstraction,
    perpEquity,
    perpStates,
    spotBalances,
    perpAllTimePnl,
}: {
    userAbstraction: WalletUserAbstraction | null;
    perpEquity: number;
    perpStates: HlCohortPerpState[];
    spotBalances: HlCohortSpotBalance[];
    perpAllTimePnl: number;
}): SegmentId[] => {
    const equity = getCohortingEquity({
        userAbstraction,
        perpEquity,
        perpStates,
        spotBalances,
    });

    return [
        labelsSegments[defineEquityDescription(Math.max(equity, 0)).label],
        labelsSegments[definePnlDescription(perpAllTimePnl).label],
    ];
};

export const HT_MAX_WIDTH_CONTENT = '1200px';
export const HEADER_HEIGHT = 80;
export const FOOTER_HEIGHT_DESKTOP = 42;
export const HT_DESKTOP_HEADER_HEIGHT = 45;

export const leaderboardsRankByValues = {
    [LEADERBOARDS.PNL_PERP_ONLY]: ['pnlAllTime', 'pnlMonth', 'pnlWeek', 'pnlDay'],
    [LEADERBOARDS.ALL_PNL]: ['pnlAllTime', 'pnlMonth', 'pnlWeek', 'pnlDay'],
    [LEADERBOARDS.AVG_DAILY_PERP_PNL]: ['avgPnl', 'highestPnl'],
    [LEADERBOARDS.BIGGEST_POSITIONS]: ['value', 'unrealizedPnl', 'funding'],
    [LEADERBOARDS.BIGGEST_30D]: [],
    [LEADERBOARDS.HIGHEST_EXPOSURE]: [],
    [LEADERBOARDS.TOP_HYPE_HOLDERS]: [],
    [LEADERBOARDS.RICH_LIST]: ['totalEquity'],
    [LEADERBOARDS.FAVORITES]: [],
    [LEADERBOARDS.TOP_CLAIMED]: ['allTimePnl'],
    [LEADERBOARDS.REFERRALS]: [
        'countReferrals',
        'countReferrals7Days',
        'cumVolume',
        'totalRewards',
        'refEarnings',
    ],
    [LEADERBOARDS.MOST_FAVORITED]: ['favoriteCount', 'favoriteCount24h'],
    [LEADERBOARDS.VAULT_LEADERBOARDS]: ['apr', 'avgFollowerPnl', 'allTimeAccountValue'],
};

export const leaderboardsOrderByValues = {
    [LEADERBOARDS.PNL_PERP_ONLY]: [
        'pnlAllTime',
        'pnlMonth',
        'pnlWeek',
        'pnlDay',
        'age',
        'perpMarginEquity',
        'openValue',
        'exposureRatio',
        'bias',
    ],
    [LEADERBOARDS.ALL_PNL]: ['pnlAllTime', 'pnlMonth', 'pnlWeek', 'pnlDay'],
    [LEADERBOARDS.AVG_DAILY_PERP_PNL]: [
        'avgPnl',
        'age',
        'greenDays',
        'volume',
        'highestPnl',
        'perpMarginEquity',
        'bias',
        'exposureRatio',
    ],
    [LEADERBOARDS.BIGGEST_POSITIONS]: [
        'side',
        'coin',
        'value',
        'entryPrice',
        'unrealizedPnl',
        'funding',
        'openTime',
        'liquidationProgress',
    ],
    [LEADERBOARDS.BIGGEST_30D]: [],
    [LEADERBOARDS.HIGHEST_EXPOSURE]: [],
    [LEADERBOARDS.TOP_HYPE_HOLDERS]: [
        'earliestActivityAt',
        'totalAmount',
        'totalAmountUsd',
        'stakedAmount',
        'stakedAmountUsd',
        'spotAmount',
        'spotAmountUsd',
        'accountValue',
    ],
    [LEADERBOARDS.FAVORITES]: [],
    [LEADERBOARDS.TOP_CLAIMED]: [
        'totalEquity',
        'perpMarginEquity',
        'allTimePnl',
        'perpAllTimePnl',
        'followersCount',
    ],
    [LEADERBOARDS.REFERRALS]: [
        'refCode',
        'countReferrals',
        'countReferrals7Days',
        'cumVolume',
        'totalRewards',
        'refEarnings',
    ],
    [LEADERBOARDS.RICH_LIST]: [
        'totalEquity',
        'perpEquity',
        'spotEquity',
        'stakingEquity',
        'vaultEquity',
        'age',
    ],
    [LEADERBOARDS.MOST_FAVORITED]: [
        'totalEquity',
        'favoriteCount',
        'favoriteCount24h',
        'age',
        'allTimePnl',
        'monthPnl',
        'weekPnl',
        'dayPnl',
    ],
    [LEADERBOARDS.VAULT_LEADERBOARDS]: [
        'apr',
        'avgFollowerPnl',
        'allTimeAccountValue',
        'allTimePnl',
        'name',
        'followersCount',
        'age',
        'leaderPerpAllTimePnl',
    ],
};

export const leaderboardsTypeUrls = {
    [LEADERBOARDS.PNL_PERP_ONLY]: 'pnl-perp-only',
    [LEADERBOARDS.ALL_PNL]: 'all-pnl',
    [LEADERBOARDS.AVG_DAILY_PERP_PNL]: 'avg-daily-perp-pnl',
    [LEADERBOARDS.BIGGEST_POSITIONS]: 'biggest-positions',
    [LEADERBOARDS.BIGGEST_30D]: 'biggest-30-d',
    [LEADERBOARDS.HIGHEST_EXPOSURE]: 'highest-exposure',
    [LEADERBOARDS.TOP_HYPE_HOLDERS]: 'top-hype-holders',
    [LEADERBOARDS.FAVORITES]: 'favorites',
    [LEADERBOARDS.TOP_CLAIMED]: 'top-claimed',
    [LEADERBOARDS.RICH_LIST]: 'rich-list',
    [LEADERBOARDS.MOST_FAVORITED]: 'most-favorited',
};

export const activeLeaderboards = [
    LEADERBOARDS.PNL_PERP_ONLY,
    LEADERBOARDS.ALL_PNL,
    LEADERBOARDS.AVG_DAILY_PERP_PNL,
    LEADERBOARDS.BIGGEST_POSITIONS,
    LEADERBOARDS.TOP_CLAIMED,
    LEADERBOARDS.REFERRALS,
    LEADERBOARDS.TOP_HYPE_HOLDERS,
    LEADERBOARDS.RICH_LIST,
    LEADERBOARDS.MOST_FAVORITED,
    LEADERBOARDS.VAULT_LEADERBOARDS,
];

export const leaderBoardsDefaultSorting = {
    [LEADERBOARDS.PNL_PERP_ONLY]: { orderBy: 'pnlMonth', rankBy: 'pnlMonth' },
    [LEADERBOARDS.ALL_PNL]: { orderBy: 'pnlAllTime', rankBy: 'pnlAllTime' },
    [LEADERBOARDS.AVG_DAILY_PERP_PNL]: { orderBy: 'avgPnl', rankBy: 'avgPnl' },
    [LEADERBOARDS.BIGGEST_POSITIONS]: { orderBy: 'value', rankBy: 'value' },
    [LEADERBOARDS.TOP_HYPE_HOLDERS]: { orderBy: 'totalAmount', rankBy: 'totalAmount' },
    [LEADERBOARDS.REFERRALS]: { orderBy: 'countReferrals', rankBy: 'countReferrals' },
    [LEADERBOARDS.TOP_CLAIMED]: { orderBy: 'allTimePnl', rankBy: 'allTimePnl' },
    [LEADERBOARDS.RICH_LIST]: { orderBy: 'totalEquity', rankBy: 'totalEquity' },
    [LEADERBOARDS.MOST_FAVORITED]: { orderBy: 'favoriteCount', rankBy: 'favoriteCount' },
    [LEADERBOARDS.VAULT_LEADERBOARDS]: { orderBy: 'avgFollowerPnl', rankBy: 'avgFollowerPnl' },
};

export const BASED_UNLIMITED_TARGET_VOLUME = 250000;
export const HL_REF_UNLIMITED_TARGET_VOLUME = 250000;
